<script>
import { isValidApPassword } from '@midulate/shared';
import UsbDeviceConnection from '../js/UsbDeviceConnection.js';
import HttpDeviceConnection, { HttpDeviceError } from '../js/HttpDeviceConnection.js';
import KnownDevices from '../js/KnownDevices.js';
import { hasWebSerial, detectTransportSupport } from '../js/transportSupport.js';
import CobsConnection from '../js/CobsConnection.js';
import Version from '../js/Version.js';
import {
  firmwareUpdateAvailable,
  canUpdateFirmwareOverConnection,
  resolveFirmwareUpdateConnection,
} from '../js/firmwareUpdate.js';

import FirmwareUpdateModal from './FirmwareUpdateModal.vue';
import FirmwareUpdateBadge from './FirmwareUpdateBadge.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPlug } from '@fortawesome/free-solid-svg-icons';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { faWifi } from '@fortawesome/free-solid-svg-icons';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-solid-svg-icons';

library.add(faPlug, faCaretDown, faFileArrowUp, faFileArrowDown, faCircleExclamation, faFloppyDisk, faWifi, faSpinner, faLink, faTrash, faCircle);

export default {
  name: "ConnectionStatus",

  components: {
    FontAwesomeIcon,
    FirmwareUpdateModal,
    FirmwareUpdateBadge
  },

  data() {
    return {
      uploading: false,
      firmwareUpdateModalOpen: false,
      // WiFi connection state
      showWifiForm: false,
      wifiHost: 'midulate.local',
      wifiProbing: false,
      wifiFound: false,
      wifiError: null,
      pendingWifi: null,
      // Known devices
      knownDevices: {},
      knownDeviceStatuses: {}, // { [uid]: { status: 'probing'|'online'|'offline', probeResult: null|object } }
    }
  },

  mounted() {
    this.updatePorts();
    // Guarded because navigator.serial is undefined outside Chromium desktop.
    // Unguarded, this threw during mount on Safari, Firefox and every browser
    // on iOS — aborting the mount before loadAndProbeKnownDevices() ran, so
    // the connection UI broke on arrival rather than merely failing to
    // connect. Three other call sites in this app already guarded it
    // (WebSerialService, firmware-install, legacy-firmware-update); this one
    // did not (MDL-392).
    if (hasWebSerial(navigator)) {
      navigator.serial.addEventListener('connect', this.updatePorts);
      navigator.serial.addEventListener('disconnect', this.updatePorts);
    }
    this.loadAndProbeKnownDevices();
  },

  beforeUnmount() {
    if (hasWebSerial(navigator)) {
      navigator.serial.removeEventListener('connect', this.updatePorts);
      navigator.serial.removeEventListener('disconnect', this.updatePorts);
    }
  },

  emits: ['deviceSelected', 'devicesUpdated', 'uploaded', 'deviceError', 'configDownloaded'],

  computed: {
    // Offering a transport the browser cannot use is worse than offering
    // nothing: it looks like the device is at fault. USB needs Web Serial;
    // WiFi needs Private Network Access, which is Chromium-only, so Safari
    // and Firefox would have the request blocked as mixed content before it
    // left the page (MDL-392).
    usbAvailable() {
      return hasWebSerial(globalThis.navigator);
    },

    wifiSearchAvailable() {
      return detectTransportSupport().wifiToDevice;
    },

    deviceNeedsUpdate() {
      return (device) => firmwareUpdateAvailable(device, this.latestFirmwareVersion);
    },

    // Gated on the device's wifi_ota capability over WiFi; USB is always
    // eligible. See canUpdateFirmwareOverConnection for the rationale.
    canUpdateOverConnection() {
      return (device) => canUpdateFirmwareOverConnection(device, this.capabilities);
    },

    // The object exposing runFirmwareUpdate() for the connected device, passed
    // to FirmwareUpdateModal as its `connection`.
    firmwareUpdateConnection() {
      return resolveFirmwareUpdateConnection(this.connectedDevice);
    },

    isConnectedDevice() {
      return (device) => {
        if (!this.connectedDevice) return false;
        if (device === this.connectedDevice) return true;
        // Fallback for reactive proxy identity edge cases with class instances:
        // compare by stable URL for WiFi devices.
        return device.baseUrl != null && device.baseUrl === this.connectedDevice.baseUrl;
      };
    },

    // Known devices not already present in attachedDevices (by uid or baseUrl).
    unattachedKnownDevices() {
      const attachedUids = new Set(this.attachedDevices.map(d => d.uid).filter(Boolean));
      const attachedUrls = new Set(this.attachedDevices.map(d => d.baseUrl).filter(Boolean));
      return Object.values(this.knownDevices).filter(entry => {
        return !attachedUids.has(entry.uid) && !attachedUrls.has(`http://${entry.host}`);
      });
    },

    apPasswordInvalid() {
      return !isValidApPassword(this.config?.settings?.wifi_ap_password);
    },
  },

  methods: {
    async connectToDevice(device) {
      this.$emit('deviceSelected', device);
    },

    async startFirmwareUpdate(device) {
      if (this.canUpdateOverConnection(device)) {
        this.firmwareUpdateModalOpen = true;
      }
    },

    async fetchLatestFirmwareVersion() {
      try {
        const res = await fetch('/firmware/midulate/versions.json');
        if (!res.ok) return;
        const { latest } = await res.json();
        if (latest) this.latestFirmwareVersion = Version.parse(latest);
      } catch (e) {
        console.warn('Could not fetch latest firmware version:', e);
      }
    },

    async prepareFirmwareUpdate(device) {
      device.prepareFirmwareUpdate();
    },

    // ---- WiFi connection ----

    openWifiForm() {
      this.showWifiForm = true;
      this.wifiFound = false;
      this.wifiError = null;
      this.pendingWifi = null;
      this.probeWifi(this.wifiHost);
    },

    closeWifiForm() {
      this.showWifiForm = false;
      this.wifiProbing = false;
      this.wifiFound = false;
      this.wifiError = null;
      this.pendingWifi = null;
    },

    async probeWifi(host) {
      const baseUrl = `http://${host}`;
      this.wifiProbing = true;
      this.wifiFound = false;
      this.wifiError = null;
      this.pendingWifi = null;
      try {
        const { token, version, uid } = await HttpDeviceConnection.probe(baseUrl, 3000);
        this.pendingWifi = { baseUrl, token, version, uid };
        this.wifiFound = true;
      } catch (e) {
        this.wifiError = `Could not reach ${host}. Check the address and try again.`;
      } finally {
        this.wifiProbing = false;
      }
    },

    async confirmWifiConnect() {
      if (!this.pendingWifi) return;
      const { baseUrl, uid, version } = this.pendingWifi;
      // Replace any existing device at the same host instead of adding a duplicate.
      const updated = this.attachedDevices.filter(d => d.baseUrl !== baseUrl);
      const device = new HttpDeviceConnection(this.pendingWifi);
      updated.push(device);
      this.$emit('devicesUpdated', updated);
      this.closeWifiForm();
      // Persist so it appears in future sessions.
      if (uid) {
        KnownDevices.save(uid, this.wifiHost, version);
        this.knownDevices = KnownDevices.getAll();
      }
    },

    // ---- Known devices ----

    loadAndProbeKnownDevices() {
      this.knownDevices = KnownDevices.getAll();
      for (const entry of Object.values(this.knownDevices)) {
        this.probeKnownDevice(entry);
      }
    },

    probeKnownDevice(entry) {
      const { uid, host } = entry;
      this.knownDeviceStatuses = { ...this.knownDeviceStatuses, [uid]: { status: 'probing', probeResult: null } };
      HttpDeviceConnection.probe(`http://${host}`, 2000)
        .then(result => {
          this.knownDeviceStatuses = {
            ...this.knownDeviceStatuses,
            [uid]: { status: 'online', probeResult: { baseUrl: `http://${host}`, ...result } },
          };
          // Keep the stored version current even without a full connect.
          KnownDevices.save(uid, host, result.version);
          this.knownDevices = KnownDevices.getAll();
        })
        .catch(() => {
          this.knownDeviceStatuses = { ...this.knownDeviceStatuses, [uid]: { status: 'offline', probeResult: null } };
        });
    },

    async connectKnownDevice(uid) {
      const entry = this.knownDevices[uid];
      if (!entry) return;
      let probeResult = this.knownDeviceStatuses[uid]?.probeResult;
      if (!probeResult) {
        try {
          const result = await HttpDeviceConnection.probe(`http://${entry.host}`, 3000);
          probeResult = { baseUrl: `http://${entry.host}`, ...result };
        } catch {
          this.wifiError = `Could not reach ${entry.host}`;
          return;
        }
      }
      const baseUrl = probeResult.baseUrl;
      const updated = this.attachedDevices.filter(d => d.baseUrl !== baseUrl);
      const device = new HttpDeviceConnection(probeResult);
      updated.push(device);
      this.$emit('devicesUpdated', updated);
      this.$emit('deviceSelected', device);
      KnownDevices.save(uid, entry.host, probeResult.version);
      this.knownDevices = KnownDevices.getAll();
    },

    removeKnownDevice(uid) {
      KnownDevices.remove(uid);
      this.knownDevices = KnownDevices.getAll();
      const statuses = { ...this.knownDeviceStatuses };
      delete statuses[uid];
      this.knownDeviceStatuses = statuses;
    },

    // ---- USB connection ----

    async updatePorts() {
      // Nothing to enumerate without Web Serial, and navigator.serial is
      // undefined outside Chromium desktop. The getPorts() call below sits in
      // a try/finally with no catch, so unguarded this rejected unhandled on
      // Safari, Firefox and iOS — called straight from mounted() (MDL-392).
      if (!hasWebSerial(navigator)) return;

      // Re-entrancy guard: plugging a device in fires one serial 'connect'
      // event per interface (two in dev mode), and each triggers this method.
      // Two overlapping runs double-open the same port — InvalidStateError
      // ("A call to open() is already in progress") and a port left broken.
      if (this._updatingPorts) return;
      this._updatingPorts = true;
      try {
        // Preserve WiFi devices — only rebuild the USB portion of the list.
        // Serial connect/disconnect events must not evict an active WiFi connection.
        const previousDevices = [...this.attachedDevices];
        const wifiDevices = previousDevices.filter(d => d.connectionType === 'http');
        const updated = [...wifiDevices];

        const allPorts = await navigator.serial.getPorts();
        for (const port of allPorts) {
          // Never probe a port that is already open: it belongs to an active
          // connection, and the probe's cleanup would close it mid-session.
          if (port.readable != null) {
            const existing = previousDevices.find(d => d.connection?.port === port);
            if (existing) updated.push(existing);
            continue;
          }
          const version = await this.requestVersion(port);
          if (version) {
            updated.push(new UsbDeviceConnection(port, version));
          }
        }

        this.$emit('devicesUpdated', updated);
      } finally {
        this._updatingPorts = false;
      }
    },

    async requestPortAccess() {
      try {
        const port = await navigator.serial.requestPort();
        this.updatePorts();
      } catch (e) {
        console.error("Port access denied:", e);
      }
    },

    async requestVersion(port) {
      const conn = new CobsConnection(port);
      try {
        await conn.open();
        const response = await conn.sendCommand('VERSION');
        await conn.close();

        const version = Version.parseVersionResponse(response);
        if (version) {
          console.log("Device found Midulate version", version);
          return version;
        }
        console.warn("Unexpected VERSION response:", response);
      } catch (e) {
        try { await conn.close(); } catch { /* already closed */ }
        console.error("Error requesting version:", e);
      }
    },

    async uploadConfig() {
      this.uploading = true;
      try {
        await this.connectedDevice.uploadConfig(this.config);
        this.uploading = false;
        this.$emit('uploaded', true);
      } catch (e) {
        this.uploading = false;
        if (e instanceof HttpDeviceError && e.isUnauthorized) {
          // Token has reset — device was rebooted.  Surface the reconnect flow.
          this.$emit('deviceError', e, this.connectedDevice);
        } else {
          console.error('Upload failed:', e);
          this.$emit('uploaded', false);
        }
      }
    }
  },

  props: {
    config: {
      type: Object,
      required: true
    },

    attachedDevices: {
      type: Array,
      required: false,
      default: () => []
    },

    connectedDevice: {
      type: Object,
      required: false,
      default: null
    },

    fileOpened: {
      type: Boolean,
      required: false,
      default: false
    },

    latestFirmwareVersion: {
      type: Object,
      required: false,
      default: null
    },

    // Capabilities of the currently connected device, as returned by
    // getCapabilities(): { capabilities_version, capabilities: { ... } }.
    // Used to gate the WiFi firmware-update affordance on `wifi_ota`.
    capabilities: {
      type: Object,
      required: false,
      default: () => ({ capabilities_version: 1, capabilities: {} })
    },

    wsConnectionState: {
      type: String,
      required: false,
      default: null
    }
  }
}
</script>
<template>
<ul class="menu menu-horizontal p-0 relative">
  <li v-if="connectedDevice && deviceNeedsUpdate(connectedDevice) && canUpdateOverConnection(connectedDevice)">
    <button class="btn btn-sm btn-outline btn-accent" @click="startFirmwareUpdate(connectedDevice)">
      <FontAwesomeIcon :icon="['fas', 'floppy-disk']" class="mr-1" />
      Update Firmware
    </button>
  </li>
  <li v-if="fileOpened">
    <button
      @click="uploadConfig"
      :disabled="uploading || wsConnectionState === 'reconnecting' || apPasswordInvalid"
      :title="apPasswordInvalid ? 'The broadcast mode password must be 8–63 characters, or blank for an open network' : null"
    >
      <FontAwesomeIcon :icon="['fas', 'file-arrow-up']" :class="(uploading) ? 'animate-spin' : ''" />
      <span v-if="!uploading"> Save to Device</span>
      <span v-else> <progress class="progress w-24"></progress></span>
    </button>
  </li>
  <li v-if="attachedDevices.length === 0 && usbAvailable">
    <a @click="requestPortAccess" class="cursor-pointer" data-testid="usb-access-menu">
      <FontAwesomeIcon :icon="['fas', 'link']" class="mr-1" />
      USB device access
    </a>
  </li>
  <li v-if="attachedDevices.length === 0 && wifiSearchAvailable" class="relative">
    <a @click="openWifiForm" class="cursor-pointer" data-testid="wifi-search-menu">
      <FontAwesomeIcon :icon="['fas', 'wifi']" class="mr-1" />
      Search for WiFi device
    </a>
    <div v-if="showWifiForm" class="absolute top-full right-0 mt-1 card bg-base-100 shadow-md p-4 w-72 dark:border dark:border-white z-50" @click.stop>
          <div class="flex justify-between items-center mb-2">
            <p class="text-sm font-semibold">Search for WiFi device</p>
            <a class="cursor-pointer text-xs" @click="closeWifiForm">✕</a>
          </div>
          <input
            v-model="wifiHost"
            type="text"
            placeholder="e.g. midulate.local or 192.168.4.1"
            class="input input-bordered input-sm w-full mb-2"
            @input="wifiFound = false; wifiError = null; pendingWifi = null"
            @keyup.enter="probeWifi(wifiHost)"
          />
          <div v-if="wifiProbing" class="text-sm text-center py-2">
            <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
            Searching for Midulate…
          </div>
          <div v-else-if="wifiFound" class="text-sm">
            <p class="mb-2 text-success">Found Midulate at <strong>{{ wifiHost }}</strong></p>
            <button class="btn btn-sm btn-primary w-full" @click="confirmWifiConnect">Connect</button>
          </div>
          <div v-else>
            <p v-if="wifiError" class="text-xs text-error mb-2">{{ wifiError }}</p>
            <button class="btn btn-sm btn-primary w-full" @click="probeWifi(wifiHost)">Search</button>
          </div>
          <template v-if="unattachedKnownDevices.length > 0">
            <div class="divider my-2 text-xs">Previously connected</div>
            <div v-for="entry in unattachedKnownDevices" :key="entry.uid" class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2 min-w-0">
                <span v-if="knownDeviceStatuses[entry.uid]?.status === 'online'"    class="status status-success shrink-0"></span>
                <span v-else-if="knownDeviceStatuses[entry.uid]?.status === 'offline'"  class="status status-error shrink-0"></span>
                <span v-else class="status status-neutral shrink-0"></span>
                <span class="text-xs truncate">{{ entry.host }}</span>
              </div>
              <div class="flex gap-1 shrink-0">
                <button class="btn btn-xs btn-primary" @click="connectKnownDevice(entry.uid)">Connect</button>
                <button class="btn btn-xs btn-ghost text-error" @click="removeKnownDevice(entry.uid)" title="Remove">
                  <FontAwesomeIcon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </template>
        </div>
  </li>

  <li v-if="attachedDevices.length > 0">
    <div :class="['dropdown dropdown-end', showWifiForm ? 'dropdown-open' : '']">
      <label tabindex="0">
        <FontAwesomeIcon v-if="wsConnectionState === 'reconnecting'" :icon="['fas', 'spinner']" class="animate-spin text-warning mr-1" />
        Devices
        <FontAwesomeIcon :icon="['fas', 'caret-down']" class="ml-2" />
      </label>

      <div tabindex="0" class="dropdown-content card bg-base-100 shadow-md p-5 w-80 dark:border dark:border-white">
        <div v-for="device in attachedDevices" class="mb-3 mt-3 gap-2 border-b pb-5 mb-3">
          <h3 class="font-russo uppercase text-lg font-stretch-ultra-condensed">Midulate</h3>

          <div>
            <div class="grid grid-cols-2">
              <div>
                <p>Version: {{ device.firmwareVersion.toString() }}</p>
                <span class="mt-1">{{ device.connection.description }}</span>
              </div>
              <div v-if="isConnectedDevice(device)">
                <div class="text-right mt-2">
                  <p v-if="wsConnectionState === 'reconnecting'" class="px-4 py-2 text-warning">
                    <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
                    Reconnecting…
                  </p>
                  <p v-else class="px-4 py-2">
                    <span class="status status-success animate-ping mr-2"></span>
                    Connected
                  </p>
                </div>
              </div>
              <div v-else>
                <p class="text-right mt-2">
                  <a class="btn" @click="connectToDevice(device)">
                    <FontAwesomeIcon :icon="['fas', 'plug']" class="mr-2" />
                    Connect
                  </a>
                </p> 
              </div>
            </div>
            <p v-if="isConnectedDevice(device)">
              <!-- The active update button now lives in the top nav next to
                   "Save to Device"; only the USB-only fallback badge (WiFi
                   device without wifi_ota) remains contextual here. -->
              <FirmwareUpdateBadge
                v-if="deviceNeedsUpdate(device) && !canUpdateOverConnection(device)"
                via-usb
                class="mt-2 w-full"
              />
            </p>
            <p v-else>
              <FirmwareUpdateBadge v-if="deviceNeedsUpdate(device)" class="mt-2 w-full" />
            </p>
          </div>
        </div>
        <template v-if="unattachedKnownDevices.length > 0">
          <div v-for="entry in unattachedKnownDevices" :key="entry.uid" class="mb-3 mt-3 gap-2 border-b pb-5">
            <div class="grid grid-cols-2">
              <h3 class="font-russo uppercase text-lg font-stretch-ultra-condensed">Midulate</h3>
              <div class="text-right">
                <button class="btn btn-xs btn-ghost text-error" @click="removeKnownDevice(entry.uid)">
                  <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-1" /> Forget
                </button>
              </div>
              <div>
                <p>Version: {{ knownDeviceStatuses[entry.uid]?.probeResult?.version ?? entry.version ?? '—' }}</p>
                <span v-if="knownDeviceStatuses[entry.uid]?.status === 'online'" class="status status-success mr-1"></span>
                <span class="mt-1 text-sm">WiFi: {{ entry.host }}</span>
              </div>
              <div class="text-right mt-2">
                <a class="btn" @click="connectKnownDevice(entry.uid)">
                  <FontAwesomeIcon :icon="['fas', 'plug']" class="mr-2" />
                  Connect
                </a>
              </div>
            </div>
          </div>
        </template>
        <div class="mt-2">
          <div v-if="wifiSearchAvailable" class="mt-2 relative">
            <a class="btn btn-info w-full" @click="openWifiForm" data-testid="wifi-search-panel">
              <FontAwesomeIcon :icon="['fas', 'wifi']" class="mr-1" />
              Search for WiFi device
            </a>
            <div v-if="showWifiForm" class="absolute bottom-full left-0 mb-1 card bg-base-100 shadow-md p-4 w-72 dark:border dark:border-white z-50" @click.stop>
                <div class="flex justify-between items-center mb-2">
                  <p class="text-sm font-semibold">Search</p>
                  <a class="cursor-pointer text-xs" @click="closeWifiForm">✕</a>
                </div>
                <input
                  v-model="wifiHost"
                  type="text"
                  placeholder="e.g. midulate.local or 192.168.4.1"
                  class="input input-bordered input-sm w-full mb-2"
                  @input="wifiFound = false; wifiError = null; pendingWifi = null"
                  @keyup.enter="probeWifi(wifiHost)"
                />
                <div v-if="wifiProbing" class="text-sm text-center py-2">
                  <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
                  Searching for Midulate…
                </div>
                <div v-else-if="wifiFound" class="text-sm">
                  <p class="mb-2 text-success">Found Midulate at <strong>{{ wifiHost }}</strong></p>
                  <button class="btn btn-sm btn-primary w-full" @click="confirmWifiConnect">Add</button>
                </div>
                <div v-else>
                  <p v-if="wifiError" class="text-xs text-error mb-2">{{ wifiError }}</p>
                  <button class="btn btn-sm btn-primary w-full" @click="probeWifi(wifiHost)">Search</button>
                </div>
              </div>
          </div>
          <a v-if="usbAvailable" class="btn btn-info w-full mt-2" @click="requestPortAccess" data-testid="usb-access-panel">
            <FontAwesomeIcon :icon="['fas', 'link']" class="mr-1" />
            USB device access
          </a>
        </div>
      </div>
    </div>
   </li> 
</ul>

<FirmwareUpdateModal
  :show="firmwareUpdateModalOpen"
  :connection="firmwareUpdateConnection"
  :currentVersion="connectedDevice?.firmwareVersion.toString()"
  @close="firmwareUpdateModalOpen = false"
  @complete="firmwareUpdateModalOpen = false"
  @error="() => {}"
/>
</template>